import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  StockTransaction,
  MaterialCatalog,
  TransactionType,
  TransactionStatus,
} from '@models/warehouse.model';
import { generateTransactionHistoryPdfFilename } from './pdfFilename';

interface TransactionHistoryPdfOptions {
  transactions: StockTransaction[];
  materials: MaterialCatalog[];
}

// Color constants (matching Tailwind theme)
const PRIMARY: [number, number, number] = [79, 70, 229]; // indigo-600
const HEADER_BG: [number, number, number] = [249, 250, 251]; // gray-50
const TEXT_PRIMARY: [number, number, number] = [17, 24, 39]; // gray-900
const TEXT_SECONDARY: [number, number, number] = [107, 114, 128]; // gray-500
const GREEN: [number, number, number] = [22, 163, 74]; // green-600
const BLUE: [number, number, number] = [37, 99, 235]; // blue-600
const PURPLE: [number, number, number] = [147, 51, 234]; // purple-600
const RED: [number, number, number] = [220, 38, 38]; // red-600
const AMBER: [number, number, number] = [217, 119, 6]; // amber-600
const CANCELLED_BG: [number, number, number] = [249, 250, 251]; // gray-50

const POSITIVE_TYPES = [
  TransactionType.RECEIVE,
  TransactionType.RETURN,
  TransactionType.RECOVERY,
  TransactionType.RELEASE_RESERVE,
];

const formatCurrency = (value?: number) => {
  if (value === undefined || value === null) return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
};

const formatDate = (date?: string | Date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatTime = (date?: string | Date) => {
  if (!date) return '';
  return new Date(date).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const getTypeLabel = (type: TransactionType): string => {
  return type.replace(/_/g, ' ');
};

const getStatusLabel = (status: TransactionStatus): string => {
  switch (status) {
    case TransactionStatus.COMPLETED:
      return 'Completed';
    case TransactionStatus.PENDING:
      return 'Pending';
    case TransactionStatus.CANCELLED:
      return 'Cancelled';
    default:
      return '-';
  }
};

export function generateTransactionHistoryPdf({
  transactions,
  materials,
}: TransactionHistoryPdfOptions): void {
  const doc = new jsPDF('landscape', 'mm', 'letter');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;

  const getMaterialName = (catalogId?: string): string => {
    if (!catalogId) return '-';
    return materials.find((m) => m.catalogId === catalogId)?.name || 'Unknown';
  };

  const getMaterialSku = (catalogId?: string): string => {
    if (!catalogId) return '';
    return materials.find((m) => m.catalogId === catalogId)?.sku || '';
  };

  const getMaterialUnit = (catalogId?: string): string => {
    if (!catalogId) return '';
    return materials.find((m) => m.catalogId === catalogId)?.unit || '';
  };

  // Sort by date descending
  const sorted = [...transactions].sort((a, b) => {
    const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return dateB - dateA;
  });

  // Summary stats
  const total = transactions.length;
  const receiveCount = transactions.filter((t) => t.type === TransactionType.RECEIVE).length;
  const consumeCount = transactions.filter((t) => t.type === TransactionType.CONSUME).length;
  const adjustCount = transactions.filter((t) => t.type === TransactionType.ADJUST).length;

  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // ========================
  // HEADER BAR
  // ========================
  doc.setFillColor(...PRIMARY);
  doc.rect(0, 0, pageWidth, 20, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('Transaction History Report', margin, 13);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(today, pageWidth - margin, 13, { align: 'right' });

  // ========================
  // SUMMARY STATS (4 cards)
  // ========================
  const statsY = 28;
  const cardGap = 4;
  const cardWidth = (pageWidth - margin * 2 - cardGap * 3) / 4;
  const cardHeight = 18;

  const stats = [
    { label: 'Total Transactions', value: String(total), color: PRIMARY },
    { label: 'Received', value: String(receiveCount), color: GREEN },
    { label: 'Consumed', value: String(consumeCount), color: BLUE },
    { label: 'Adjusted', value: String(adjustCount), color: PURPLE },
  ];

  stats.forEach((stat, i) => {
    const x = margin + i * (cardWidth + cardGap);

    // Card bg
    doc.setFillColor(245, 245, 250);
    doc.roundedRect(x, statsY, cardWidth, cardHeight, 2, 2, 'F');

    // Label
    doc.setTextColor(...TEXT_SECONDARY);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(stat.label, x + 4, statsY + 7);

    // Value
    doc.setTextColor(...stat.color);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(stat.value, x + 4, statsY + 14);
  });

  // ========================
  // TABLE
  // ========================
  const tableColumns = [
    { header: 'Date', dataKey: 'date' },
    { header: 'Type', dataKey: 'type' },
    { header: 'Material', dataKey: 'material' },
    { header: 'Quantity', dataKey: 'quantity' },
    { header: 'Unit Cost', dataKey: 'unitCost' },
    { header: 'Total', dataKey: 'total' },
    { header: 'Reference', dataKey: 'reference' },
    { header: 'Status', dataKey: 'status' },
  ];

  const tableRows = sorted.map((t) => {
    const isPositive = POSITIVE_TYPES.includes(t.type);
    const sign = isPositive ? '+' : '-';
    const unit = getMaterialUnit(t.catalogId);
    const quantityText = `${sign}${Math.abs(t.quantity)}${unit ? ` ${unit}` : ''}`;

    let totalText = '-';
    if (t.totalCost) {
      const totalSign = isPositive ? '+' : '-';
      totalText = `${totalSign}${formatCurrency(Math.abs(t.totalCost))}`;
    }

    return {
      date: `${formatDate(t.createdAt)}\n${formatTime(t.createdAt)}`,
      type: getTypeLabel(t.type),
      material: getMaterialName(t.catalogId) + (getMaterialSku(t.catalogId) ? `\n${getMaterialSku(t.catalogId)}` : ''),
      quantity: quantityText,
      unitCost: formatCurrency(t.unitCost),
      total: totalText,
      reference: t.referenceNumber || t.notes || '-',
      status: getStatusLabel(t.status),
      _type: t.type,
      _status: t.status,
      _isPositive: isPositive,
    };
  });

  autoTable(doc, {
    startY: statsY + cardHeight + 8,
    columns: tableColumns,
    body: tableRows,
    margin: { left: margin, right: margin },
    styles: {
      fontSize: 8,
      cellPadding: 3,
      textColor: TEXT_PRIMARY,
      lineColor: [229, 231, 235],
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: HEADER_BG,
      textColor: TEXT_SECONDARY,
      fontStyle: 'bold',
      fontSize: 7,
      halign: 'left',
    },
    columnStyles: {
      quantity: { halign: 'right' },
      unitCost: { halign: 'right' },
      total: { halign: 'right' },
      status: { halign: 'center' },
    },
    alternateRowStyles: {
      fillColor: [255, 255, 255],
    },
    didParseCell: (data) => {
      if (data.section === 'body') {
        const row = tableRows[data.row.index];
        if (!row) return;

        // Cancelled row styling
        if (row._status === TransactionStatus.CANCELLED) {
          data.cell.styles.fillColor = CANCELLED_BG;
          data.cell.styles.textColor = TEXT_SECONDARY;
        }

        // Quantity column color coding
        if (data.column.dataKey === 'quantity') {
          data.cell.styles.textColor = row._isPositive ? GREEN : RED;
          data.cell.styles.fontStyle = 'bold';
        }

        // Total column color coding
        if (data.column.dataKey === 'total' && row.total !== '-') {
          data.cell.styles.textColor = row._isPositive ? GREEN : RED;
          data.cell.styles.fontStyle = 'bold';
        }

        // Status column color coding
        if (data.column.dataKey === 'status') {
          if (row._status === TransactionStatus.COMPLETED) {
            data.cell.styles.textColor = GREEN;
          } else if (row._status === TransactionStatus.PENDING) {
            data.cell.styles.textColor = AMBER;
          } else if (row._status === TransactionStatus.CANCELLED) {
            data.cell.styles.textColor = RED;
          }
          data.cell.styles.fontStyle = 'bold';
        }
      }
    },
    didDrawPage: () => {
      const pageNumber = doc.getCurrentPageInfo().pageNumber;

      // Footer line
      doc.setDrawColor(229, 231, 235);
      doc.setLineWidth(0.5);
      doc.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);

      // Footer text
      doc.setFontSize(7);
      doc.setTextColor(...TEXT_SECONDARY);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated on ${today}`, margin, pageHeight - 7);
      doc.text(
        `Page ${pageNumber} of {total_pages}`,
        pageWidth - margin,
        pageHeight - 7,
        { align: 'right' },
      );
    },
  });

  // Replace total pages placeholder
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.putTotalPages('{total_pages}');
  }

  // Save
  const filename = generateTransactionHistoryPdfFilename();
  doc.save(filename);
}
